import {getUserData} from 'utils/functions/getUserData/getUserData';
import {useState} from 'react';
import {Modal} from 'components/ui';
import OlympicsRegistrationResponse from 'containers/Login/views/OlympicsRegistration/views/OlympicsRegistrationResponse';
import ConfirmOlympicsUserInfo from 'containers/Login/views/OlympicsRegistration/views/ConfirmOlympicsUserInfo';
import {FormProvider, useForm} from 'react-hook-form';
import {
  acceptContestRequest,
  fetchContestRequest,
} from 'APIs/letrusAPI/contests';
import {guardianConsentRequest} from 'APIs/letrusAPI/guardianConsent';
import {phoneMask} from 'utils/functions/validation/mask';
import {CTABanner} from '../CTABannersCarousel';
import styles from './OlympicsBanner.module.scss';

type ContestsBanners = CTABanner[] | undefined;

interface ContestsBannerProps {
  priority: number;
}

function useContestsBanner({priority}: ContestsBannerProps): ContestsBanners {
  const {user, userProfile} = getUserData();

  const [activeModalView, setActiveModalView] = useState('none');

  const {userRole} = getUserData();

  const contests = fetchContestRequest();
  const acceptContest = acceptContestRequest();
  const guardianConsent = guardianConsentRequest();

  const form = useForm({
    defaultValues: {
      school_grade: userRole?.school_grade_name,
      first_name: user?.first_name,
      last_name: user?.last_name,
      birth_date: userProfile?.birth,
      email: user?.email,
      accept_terms: true,
      phone_number: phoneMask(String(userProfile?.phone_number)),
    },
    mode: 'onChange',
    reValidateMode: 'onBlur',
  });

  async function handleSubmit(formData, contestId: number) {
    setActiveModalView('registration-response');

    if (Object.keys(formData).some((key) => key.includes('guardian'))) {
      const guardianData = {
        user: user?.id,
        contest: contestId,
        guardian_cpf: formData.guardian_cpf,
        guardian_name: formData.guardian_name,
        guardian_email: formData.guardian_email,
        policy_version: `privacy:${userProfile?.terms_privacy_version_id}, use:${userProfile?.terms_use_version_id}`,
        guardian_relationship: formData.guardian_relationship,
      };

      await guardianConsent.trigger(guardianData);
    }

    acceptContest.trigger({id: contestId});
  }

  function handleModalClose(status?: string) {
    setActiveModalView('none');
    form.reset();

    if (status === 'success') {
      contests.mutate();
    }
  }

  return contests.data?.results.map((contest) => ({
    priority,
    title: contest.banner_title,
    description: contest.banner_message,
    theme: 'light',
    color: '#7518f7',
    icon: ['fad', 'trophy'],
    isVisible: () => true,
    action: {
      text: 'participar',
      onClick: () => setActiveModalView('confirm-data'),
    },
    modals: (
      <>
        <Modal
          clickOutsideToClose={false}
          isOpen={
            activeModalView === 'confirm-data' ||
            activeModalView === 'registration-response'
          }
          onClose={() => handleModalClose()}
        >
          {/* eslint-disable-next-line react/jsx-props-no-spreading */}
          <FormProvider {...form}>
            <form
              onSubmit={form.handleSubmit((formData) =>
                handleSubmit(formData, contest.id),
              )}
              className={styles.modalForm}
            >
              {activeModalView === 'confirm-data' && (
                <ConfirmOlympicsUserInfo
                  onGoBack={() => setActiveModalView('none')}
                />
              )}
              {activeModalView === 'registration-response' && (
                <OlympicsRegistrationResponse
                  isLoading={
                    acceptContest.isMutating || guardianConsent.isMutating
                  }
                  onFinish={() => handleModalClose('success')}
                  onGoBack={() => setActiveModalView('confirm-data')}
                  isSuccess={!acceptContest.error && !guardianConsent.error}
                />
              )}
            </form>
          </FormProvider>
        </Modal>
      </>
    ),
  }));
}

export default useContestsBanner;
